import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { NormalizedApiError } from '@/shared/api';

import * as budgetsApi from '../api/budgetsApi';
import * as campaignsApi from '../api/campaignsApi';
import type { ListCampaignsParams } from '../api/campaignsApi';
import * as couponsApi from '../api/couponsApi';
import type { ListCouponsParams } from '../api/couponsApi';
import * as policiesApi from '../api/policiesApi';
import * as productSetsApi from '../api/productSetsApi';
import type { ListProductSetsParams } from '../api/productSetsApi';
import * as promotionsApi from '../api/promotionsApi';
import type { ListPromotionAuditParams, ListPromotionsParams } from '../api/promotionsApi';
import * as refApi from '../api/refApi';
import type { SearchProductsParams } from '../api/refApi';
import * as simulateApi from '../api/simulateApi';
import type {
  BudgetRequest,
  BudgetResponse,
  CampaignRequest,
  CampaignResponse,
  CategoryRef,
  CouponRequest,
  CouponResponse,
  CustomerGroupRef,
  GenerateCodesRequest,
  MaterializeResult,
  Page,
  ProductRef,
  ProductSetRequest,
  ProductSetResponse,
  PromoProtectionPolicyRequest,
  PromoProtectionPolicyResponse,
  PromotionRequest,
  PromotionResponse,
  PromotionStatsResponse,
  SalesChannelRef,
  SimulateRequest,
  SimulateResponse,
  StackingPolicyRequest,
  StackingPolicyResponse,
} from '../model/types';
import { promoKeys } from './queryKeys';

type Opts = { enabled?: boolean };
type Err = NormalizedApiError;

// ── Campaigns ───────────────────────────────────────────────────────────────────────────────────
export function useCampaigns(params: ListCampaignsParams = {}, options?: Opts) {
  return useQuery<Awaited<ReturnType<typeof campaignsApi.listCampaigns>>, Err>({
    queryKey: promoKeys.campaignsList(params),
    queryFn: () => campaignsApi.listCampaigns(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCampaign(campaignId: string | undefined, options?: Opts) {
  return useQuery<CampaignResponse, Err>({
    queryKey: promoKeys.campaign(campaignId ?? ''),
    queryFn: () => campaignsApi.getCampaign(campaignId as string),
    enabled: (options?.enabled ?? true) && Boolean(campaignId),
  });
}

function invalidateCampaigns(qc: ReturnType<typeof useQueryClient>, campaignId?: string) {
  void qc.invalidateQueries({ queryKey: promoKeys.campaigns });
  if (campaignId) void qc.invalidateQueries({ queryKey: promoKeys.campaign(campaignId) });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignResponse, Err, CampaignRequest>({
    mutationFn: campaignsApi.createCampaign,
    onSuccess: () => invalidateCampaigns(qc),
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignResponse, Err, { campaignId: string; body: CampaignRequest }>({
    mutationFn: ({ campaignId, body }) => campaignsApi.updateCampaign(campaignId, body),
    onSuccess: (data) => invalidateCampaigns(qc, data.id),
  });
}

export function useActivateCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignResponse, Err, string>({
    mutationFn: campaignsApi.activateCampaign,
    onSuccess: (data) => invalidateCampaigns(qc, data.id),
  });
}

export function usePauseCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignResponse, Err, { campaignId: string; reason?: string }>({
    mutationFn: ({ campaignId, reason }) => campaignsApi.pauseCampaign(campaignId, reason),
    onSuccess: (data) => {
      invalidateCampaigns(qc, data.id);
      void qc.invalidateQueries({ queryKey: promoKeys.promotions });
    },
  });
}

export function useEndCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignResponse, Err, string>({
    mutationFn: campaignsApi.endCampaign,
    onSuccess: (data) => invalidateCampaigns(qc, data.id),
  });
}

export function useArchiveCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignResponse, Err, string>({
    mutationFn: campaignsApi.archiveCampaign,
    onSuccess: (data) => invalidateCampaigns(qc, data.id),
  });
}

// ── Promotions ──────────────────────────────────────────────────────────────────────────────────
export function usePromotions(params: ListPromotionsParams = {}, options?: Opts) {
  return useQuery<Awaited<ReturnType<typeof promotionsApi.listPromotions>>, Err>({
    queryKey: promoKeys.promotionsList(params),
    queryFn: () => promotionsApi.listPromotions(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function usePromotion(promotionId: string | undefined, options?: Opts) {
  return useQuery<PromotionResponse, Err>({
    queryKey: promoKeys.promotion(promotionId ?? ''),
    queryFn: () => promotionsApi.getPromotion(promotionId as string),
    enabled: (options?.enabled ?? true) && Boolean(promotionId),
  });
}

function invalidatePromotions(qc: ReturnType<typeof useQueryClient>, promotionId?: string) {
  void qc.invalidateQueries({ queryKey: promoKeys.promotions });
  if (promotionId) void qc.invalidateQueries({ queryKey: promoKeys.promotion(promotionId) });
}

export function useCreatePromotion() {
  const qc = useQueryClient();
  return useMutation<PromotionResponse, Err, PromotionRequest>({
    mutationFn: promotionsApi.createPromotion,
    onSuccess: () => invalidatePromotions(qc),
  });
}

export function useUpdatePromotion() {
  const qc = useQueryClient();
  return useMutation<PromotionResponse, Err, { promotionId: string; body: PromotionRequest }>({
    mutationFn: ({ promotionId, body }) => promotionsApi.updatePromotion(promotionId, body),
    onSuccess: (data) => invalidatePromotions(qc, data.id),
  });
}

export function useSubmitPromotion() {
  const qc = useQueryClient();
  return useMutation<PromotionResponse, Err, string>({
    mutationFn: promotionsApi.submitPromotion,
    onSuccess: (data) => invalidatePromotions(qc, data.id),
  });
}

export function useApprovePromotion() {
  const qc = useQueryClient();
  return useMutation<PromotionResponse, Err, string>({
    mutationFn: promotionsApi.approvePromotion,
    onSuccess: (data) => invalidatePromotions(qc, data.id),
  });
}

export function useRejectPromotion() {
  const qc = useQueryClient();
  return useMutation<PromotionResponse, Err, { promotionId: string; reason?: string }>({
    mutationFn: ({ promotionId, reason }) => promotionsApi.rejectPromotion(promotionId, reason),
    onSuccess: (data) => invalidatePromotions(qc, data.id),
  });
}

export function useActivatePromotion() {
  const qc = useQueryClient();
  return useMutation<PromotionResponse, Err, string>({
    mutationFn: promotionsApi.activatePromotion,
    onSuccess: (data) => invalidatePromotions(qc, data.id),
  });
}

export function usePausePromotion() {
  const qc = useQueryClient();
  return useMutation<PromotionResponse, Err, { promotionId: string; reason?: string }>({
    mutationFn: ({ promotionId, reason }) => promotionsApi.pausePromotion(promotionId, reason),
    onSuccess: (data) => invalidatePromotions(qc, data.id),
  });
}

export function useEndPromotion() {
  const qc = useQueryClient();
  return useMutation<PromotionResponse, Err, string>({
    mutationFn: promotionsApi.endPromotion,
    onSuccess: (data) => invalidatePromotions(qc, data.id),
  });
}

export function useArchivePromotion() {
  const qc = useQueryClient();
  return useMutation<PromotionResponse, Err, string>({
    mutationFn: promotionsApi.archivePromotion,
    onSuccess: (data) => invalidatePromotions(qc, data.id),
  });
}

export function usePromotionAudit(
  promotionId: string | undefined,
  params: ListPromotionAuditParams = {},
  options?: Opts,
) {
  return useQuery<Awaited<ReturnType<typeof promotionsApi.listPromotionAudit>>, Err>({
    queryKey: promoKeys.promotionAudit(promotionId ?? '', params),
    queryFn: () => promotionsApi.listPromotionAudit(promotionId as string, params),
    enabled: (options?.enabled ?? true) && Boolean(promotionId),
    placeholderData: keepPreviousData,
  });
}

/** Redemption count / discount spend / attributed revenue for the Statistics panel on the promotion
 * detail page (`PromotionStatsPanel`) — only meaningful for an already-saved promotion. */
export function usePromotionStats(promotionId: string | undefined, options?: Opts) {
  return useQuery<PromotionStatsResponse, Err>({
    queryKey: promoKeys.promotionStats(promotionId ?? ''),
    queryFn: () => promotionsApi.getPromotionStats(promotionId as string),
    enabled: (options?.enabled ?? true) && Boolean(promotionId),
  });
}

/**
 * Fetch full promotion detail for a small set of ids (`useQueries`) — the list-summary DTO
 * (`PromotionSummary`) carries no conditions/actions, so the approval queue's per-row discount
 * summary needs each pending promotion's full detail. Only ever called with a small (queue-sized)
 * id list, so the N+1 here is deliberate and bounded, unlike the full promotions list.
 */
export function usePromotionsForIds(promotionIds: string[]): UseQueryResult<PromotionResponse, Err>[] {
  return useQueries({
    queries: promotionIds.map((id) => ({
      queryKey: promoKeys.promotion(id),
      queryFn: () => promotionsApi.getPromotion(id),
    })),
  }) as UseQueryResult<PromotionResponse, Err>[];
}

// ── Product sets ────────────────────────────────────────────────────────────────────────────────
export function useProductSets(params: ListProductSetsParams = {}, options?: Opts) {
  return useQuery<Awaited<ReturnType<typeof productSetsApi.listProductSets>>, Err>({
    queryKey: promoKeys.productSetsList(params),
    queryFn: () => productSetsApi.listProductSets(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useProductSet(productSetId: string | undefined, options?: Opts) {
  return useQuery<ProductSetResponse, Err>({
    queryKey: promoKeys.productSet(productSetId ?? ''),
    queryFn: () => productSetsApi.getProductSet(productSetId as string),
    enabled: (options?.enabled ?? true) && Boolean(productSetId),
  });
}

export function useCreateProductSet() {
  const qc = useQueryClient();
  return useMutation<ProductSetResponse, Err, ProductSetRequest>({
    mutationFn: productSetsApi.createProductSet,
    onSuccess: () => void qc.invalidateQueries({ queryKey: promoKeys.productSets }),
  });
}

export function useUpdateProductSet() {
  const qc = useQueryClient();
  return useMutation<ProductSetResponse, Err, { productSetId: string; body: ProductSetRequest }>({
    mutationFn: ({ productSetId, body }) => productSetsApi.updateProductSet(productSetId, body),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: promoKeys.productSets });
      void qc.invalidateQueries({ queryKey: promoKeys.productSet(data.id) });
    },
  });
}

export function useMaterializeProductSet() {
  const qc = useQueryClient();
  return useMutation<MaterializeResult, Err, string>({
    mutationFn: productSetsApi.materializeProductSet,
    onSuccess: (data) =>
      void qc.invalidateQueries({ queryKey: promoKeys.productSet(data.productSetId) }),
  });
}

// ── Coupons ─────────────────────────────────────────────────────────────────────────────────────
export function useCoupons(params: ListCouponsParams = {}, options?: Opts) {
  return useQuery<Awaited<ReturnType<typeof couponsApi.listCoupons>>, Err>({
    queryKey: promoKeys.couponsList(params),
    queryFn: () => couponsApi.listCoupons(params),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useCoupon(couponId: string | undefined, options?: Opts) {
  return useQuery<CouponResponse, Err>({
    queryKey: promoKeys.coupon(couponId ?? ''),
    queryFn: () => couponsApi.getCoupon(couponId as string),
    enabled: (options?.enabled ?? true) && Boolean(couponId),
  });
}

function invalidateCoupons(qc: ReturnType<typeof useQueryClient>, couponId?: string) {
  void qc.invalidateQueries({ queryKey: promoKeys.coupons });
  if (couponId) void qc.invalidateQueries({ queryKey: promoKeys.coupon(couponId) });
}

export function useCreateCoupon() {
  const qc = useQueryClient();
  return useMutation<CouponResponse, Err, CouponRequest>({
    mutationFn: couponsApi.createCoupon,
    onSuccess: () => invalidateCoupons(qc),
  });
}

export function useActivateCoupon() {
  const qc = useQueryClient();
  return useMutation<CouponResponse, Err, string>({
    mutationFn: couponsApi.activateCoupon,
    onSuccess: (data) => invalidateCoupons(qc, data.id),
  });
}

export function usePauseCoupon() {
  const qc = useQueryClient();
  return useMutation<CouponResponse, Err, string>({
    mutationFn: couponsApi.pauseCoupon,
    onSuccess: (data) => invalidateCoupons(qc, data.id),
  });
}

export function useEndCoupon() {
  const qc = useQueryClient();
  return useMutation<CouponResponse, Err, string>({
    mutationFn: couponsApi.endCoupon,
    onSuccess: (data) => invalidateCoupons(qc, data.id),
  });
}

export function useCouponCodes(couponId: string | undefined, options?: Opts) {
  return useQuery<Awaited<ReturnType<typeof couponsApi.listCouponCodes>>, Err>({
    queryKey: promoKeys.couponCodes(couponId ?? ''),
    queryFn: () => couponsApi.listCouponCodes(couponId as string),
    enabled: (options?.enabled ?? true) && Boolean(couponId),
  });
}

export function useGenerateCouponCodes() {
  const qc = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof couponsApi.generateCouponCodes>>,
    Err,
    { couponId: string; body: GenerateCodesRequest }
  >({
    mutationFn: ({ couponId, body }) => couponsApi.generateCouponCodes(couponId, body),
    onSuccess: (_data, { couponId }) => {
      void qc.invalidateQueries({ queryKey: promoKeys.couponCodes(couponId) });
      void qc.invalidateQueries({ queryKey: promoKeys.coupon(couponId) });
    },
  });
}

export function useExportCouponCodes() {
  return useMutation<string, Err, string>({ mutationFn: couponsApi.exportCouponCodes });
}

export function useDeactivateCouponCode() {
  const qc = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof couponsApi.deactivateCouponCode>>,
    Err,
    { couponId: string; codeId: string }
  >({
    mutationFn: ({ couponId, codeId }) => couponsApi.deactivateCouponCode(couponId, codeId),
    onSuccess: (_data, { couponId }) =>
      void qc.invalidateQueries({ queryKey: promoKeys.couponCodes(couponId) }),
  });
}

// ── Budgets ─────────────────────────────────────────────────────────────────────────────────────
export function useBudget(budgetId: string | undefined, options?: Opts) {
  return useQuery<BudgetResponse, Err>({
    queryKey: promoKeys.budget(budgetId ?? ''),
    queryFn: () => budgetsApi.getBudget(budgetId as string),
    enabled: (options?.enabled ?? true) && Boolean(budgetId),
  });
}

export function useBudgetBurn(budgetId: string | undefined, options?: Opts) {
  return useQuery<BudgetResponse, Err>({
    queryKey: promoKeys.budgetBurn(budgetId ?? ''),
    queryFn: () => budgetsApi.getBudgetBurn(budgetId as string),
    enabled: (options?.enabled ?? true) && Boolean(budgetId),
  });
}

export function useCreateBudget() {
  const qc = useQueryClient();
  return useMutation<BudgetResponse, Err, BudgetRequest>({
    mutationFn: budgetsApi.createBudget,
    onSuccess: (data) => {
      qc.setQueryData(promoKeys.budget(data.id), data);
      // No list-budgets endpoint exists (see api/budgetsApi.ts) — the campaigns list is one of
      // the panel's two discovery paths, so refresh it in case this budget gets attached to one.
      void qc.invalidateQueries({ queryKey: promoKeys.campaigns });
    },
  });
}

/**
 * Fetch a set of budgets by id in parallel (`useQueries`, not a loop of `useBudget` — hooks can't
 * be called in a loop). Used by `BudgetsPanel` to resolve the budgets discoverable via campaigns'
 * `budgetId` (see `api/budgetsApi.ts` for why there is no list-budgets endpoint to page through
 * instead).
 */
export function useBudgetsForIds(budgetIds: string[]): UseQueryResult<BudgetResponse, Err>[] {
  return useQueries({
    queries: budgetIds.map((id) => ({
      queryKey: promoKeys.budget(id),
      queryFn: () => budgetsApi.getBudget(id),
    })),
  }) as UseQueryResult<BudgetResponse, Err>[];
}

// ── Stacking / protection policy ────────────────────────────────────────────────────────────────
export function useStackingPolicy(options?: Opts) {
  return useQuery<StackingPolicyResponse, Err>({
    queryKey: promoKeys.stackingPolicy,
    queryFn: policiesApi.getStackingPolicy,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpdateStackingPolicy() {
  const qc = useQueryClient();
  return useMutation<StackingPolicyResponse, Err, StackingPolicyRequest>({
    mutationFn: policiesApi.updateStackingPolicy,
    onSuccess: (data) => qc.setQueryData(promoKeys.stackingPolicy, data),
  });
}

export function usePromoProtectionPolicy(options?: Opts) {
  return useQuery<PromoProtectionPolicyResponse, Err>({
    queryKey: promoKeys.protectionPolicy,
    queryFn: policiesApi.getPromoProtectionPolicy,
    enabled: options?.enabled ?? true,
    retry: false,
  });
}

export function useUpdatePromoProtectionPolicy() {
  const qc = useQueryClient();
  return useMutation<PromoProtectionPolicyResponse, Err, PromoProtectionPolicyRequest>({
    mutationFn: policiesApi.updatePromoProtectionPolicy,
    onSuccess: (data) => qc.setQueryData(promoKeys.protectionPolicy, data),
  });
}

// ── Simulation ──────────────────────────────────────────────────────────────────────────────────
export function useSimulatePromoCart() {
  return useMutation<SimulateResponse, Err, SimulateRequest>({
    mutationFn: simulateApi.simulatePromoCart,
  });
}

// ── Reference data ──────────────────────────────────────────────────────────────────────────────
export function useProductSearch(params: SearchProductsParams, options?: Opts) {
  return useQuery<Page<ProductRef>, Err>({
    queryKey: promoKeys.productSearch(params),
    queryFn: () => refApi.searchProducts(params),
    enabled: (options?.enabled ?? true) && Boolean(params.query && params.query.trim().length >= 2),
    placeholderData: keepPreviousData,
  });
}

export function useAllCategories(options?: Opts) {
  return useQuery<CategoryRef[], Err>({
    queryKey: promoKeys.categories,
    queryFn: refApi.listAllCategories,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

export function usePromoSalesChannels(options?: Opts) {
  return useQuery<SalesChannelRef[], Err>({
    queryKey: promoKeys.salesChannels,
    queryFn: refApi.listSalesChannels,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

export function usePromoCustomerGroups(options?: Opts) {
  return useQuery<CustomerGroupRef[], Err>({
    queryKey: promoKeys.customerGroups,
    queryFn: refApi.listCustomerGroups,
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}
